import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AiFillProduct, AiOutlineAudit } from 'react-icons/ai'
import { FaBell, FaSignOutAlt, FaUser } from 'react-icons/fa'
import { FaListCheck, FaUsers } from 'react-icons/fa6'
import { ImProfile } from 'react-icons/im'
import { IoStorefrontSharp } from 'react-icons/io5'
import { MdOutlineMenu } from 'react-icons/md'
import { TbReportAnalytics } from 'react-icons/tb'

import { useAuth } from '../hooks/useAuth'
import { usePermission } from '../hooks/usePermission'
import { getNotifications, markNotificationAsSeen, Notification } from '../services/appServices'
import { PendingUpload } from '../utils/indexedDB/addPendingUpload'
import { getPendingUploads } from '../utils/indexedDB/getPendingUploads'
import { Button } from './Button'
import { DropdownMenuItem } from './DropdownMenuItem'
import { Dropdown } from './Drowdown'
import { NotificationItem } from './NotificationItem'
import { NotificationPendingUpload } from './NotificationPendingUpload'

export function HeaderActions() {
  const [hasPendingUploads, setHasPendingUploads] = useState(false)
  const [isRetryingUpload, setIsRetryingUpload] = useState(false)

  const [userNotifications, setUserNotifications] = useState<Notification[]>([])

  const permissions = usePermission()
  const { signOut } = useAuth()

  const { data: notifications } = useQuery({
    queryKey: ['user_notifications'],
    queryFn: () => getNotifications(),
    refetchOnWindowFocus: 'always',
  })

  function hasUnseenNotification() {
    if (!notifications) return
    return notifications.some(item => item.notification_recipient && !item.notification_recipient[0].viewed_at)
  }

  async function markAsSeen(notification_id: string) {
    const result = await markNotificationAsSeen(notification_id)
    if (result) {
      if (result.success) {
        const notificationsCopy = [...userNotifications]
        const notificationIndex = notificationsCopy.findIndex(item => item.id === notification_id)
        if (notificationsCopy[notificationIndex].notification_recipient && notificationsCopy[notificationIndex].notification_recipient[0]) {
          notificationsCopy[notificationIndex].notification_recipient[0].viewed_at = result.notification?.viewed_at ?? new Date()
        }
        toast.success('Notificação marcada como lida', { position: 'top-left' })
        setUserNotifications(notificationsCopy)
      } else {
        toast.error('Notificação marcada como lida', { position: 'top-left' })
      }
    }
  }

  function handleSetIsRetryingUpload(value: boolean) {
    setIsRetryingUpload(value)
  }

  useEffect(() => {
    const pendingUploads = getPendingUploads() as unknown as PendingUpload[][]
    if (pendingUploads) {
      setHasPendingUploads(pendingUploads && pendingUploads.length > 0)
    }
  }, [isRetryingUpload])

  useEffect(() => {
    if (notifications) {
      setUserNotifications(notifications)
    }
  }, [notifications])

  return (
    <div className="flex space-x-2">
      <div className="relative flex flex-row space-x-2">
        {hasPendingUploads
          ? (
            <>
              <div className="absolute h-2 w-2 rounded-full bg-indigo-500 animate-ping right-2 top-2 z-[999]" />
              <div className="absolute h-2 w-2 rounded-full bg-indigo-500 right-2 top-2 z-[999]" />
            </>
            )
          : (
            <>
              {hasUnseenNotification() && (
                <>
                  <div className="absolute h-2 w-2 rounded-full bg-red-500 animate-ping right-2 top-2 z-[999]" />
                  <div className="absolute h-2 w-2 rounded-full bg-red-500 right-2 top-2 z-[999]" />
                </>
              )}
            </>
            )}
        <Dropdown
          icon={FaBell}
          containerClassName="-right-[107px]"
        >
          <div className="max-h-[200px] overflow-y-auto rounded-md">
            {hasPendingUploads && (
              <NotificationPendingUpload handleSetIsRetryingUpload={handleSetIsRetryingUpload} />
            )}
            {userNotifications && userNotifications.length > 0 && userNotifications.map(item => (
              <NotificationItem key={item.id} notification={item} markAsSeen={markAsSeen} />
            ))}
          </div>
        </Dropdown>
      </div>
      <nav className="flex flex-row space-x-2">
        <Dropdown title="Menu" icon={MdOutlineMenu}>
          <DropdownMenuItem
            to="/reports"
            title="Relatórios"
            icon={TbReportAnalytics}
            hasPermission={permissions.reports}
          />
          <DropdownMenuItem
            to="/users"
            title="Usuários"
            icon={FaUser}
            hasPermission={permissions.users}
          />
          <DropdownMenuItem
            to="/stores"
            title="Lojas"
            icon={IoStorefrontSharp}
            hasPermission={permissions.stores}
          />
          <DropdownMenuItem
            to="/promoters-companies"
            title="Empresa de promotores"
            icon={FaUsers}
            hasPermission={permissions.promotersCompany}
          />
          <DropdownMenuItem
            to="/products"
            title="Produtos"
            icon={AiFillProduct}
            hasPermission={permissions.products}
          />
          <DropdownMenuItem
            to="/trade-marketing-daily-check"
            title="Trade Marketing"
            icon={FaListCheck}
            hasPermission={permissions.tradeMarketingDailyCheck}
          />
          <DropdownMenuItem
            to="/logs"
            title="Logs"
            icon={AiOutlineAudit}
            hasPermission={permissions.logs}
          />
          <DropdownMenuItem
            to="/profile"
            title="Meu Perfil"
            icon={ImProfile}
            hasPermission={permissions.profile}
          />
          <Button
            onClick={() => signOut()}
            className="flex items-center justify-between gap-8 p-3 hover:bg-slate-300 border-t rounded-none"
            variant="ghost"
          >
            <FaSignOutAlt />
            Sair
          </Button>
        </Dropdown>
      </nav>
    </div>
  )
}
